/**
 * Shared Zod schemas for common validation patterns
 * These schemas reduce duplication across tRPC routers
 */
import { z } from 'zod';
// ============================================================================
// Pagination Schemas
// ============================================================================
/**
 * Standard cursor-based pagination input
 * Use for infinite scroll / load more patterns
 */
export const cursorPaginationSchema = z.object({
    limit: z.number().min(1).max(100).default(20),
    cursor: z.string().optional(),
});
/**
 * Standard offset-based pagination input
 * Use for traditional page navigation
 */
export const offsetPaginationSchema = z.object({
    limit: z.number().min(1).max(100).default(20),
    offset: z.number().min(0).default(0),
});
// ============================================================================
// Date Range Schemas
// ============================================================================
/**
 * Date range filter with optional bounds
 */
export const dateRangeSchema = z.object({
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
});
/**
 * Date range with required bounds
 */
export const requiredDateRangeSchema = z.object({
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
});
/**
 * Days-based lookback filter
 */
export const daysFilterSchema = z.object({
    days: z.number().min(1).default(30),
});
// ============================================================================
// ID Schemas
// ============================================================================
/**
 * Single ID input
 */
export const idSchema = z.object({
    id: z.string().min(1),
});
/**
 * Multiple IDs input
 */
export const idsSchema = z.object({
    ids: z.array(z.string().min(1)).min(1),
});
// ============================================================================
// Search Schemas
// ============================================================================
/**
 * Basic search with term
 */
export const searchSchema = z.object({
    searchTerm: z.string().optional(),
});
/**
 * Search with pagination
 */
export const searchWithPaginationSchema = cursorPaginationSchema.merge(searchSchema);
// ============================================================================
// Tags Schemas
// ============================================================================
/**
 * Tags array schema
 */
export const tagsSchema = z.object({
    tags: z.array(z.string()).default([]),
});
/**
 * Tags filter (for querying)
 */
export const tagsFilterSchema = z.object({
    tags: z.array(z.string()).optional(),
});
// ============================================================================
// Common Field Schemas
// ============================================================================
/**
 * Title with length constraints
 */
export const titleSchema = z.string().min(1, 'Title is required').max(200);
/**
 * Optional title
 */
export const optionalTitleSchema = z.string().min(1).max(200).optional();
/**
 * Content/description field
 */
export const contentSchema = z.string().min(1, 'Content is required');
/**
 * Optional content
 */
export const optionalContentSchema = z.string().min(1).optional();
/**
 * Notes field (optional by default)
 */
export const notesSchema = z.string().nullish();
/**
 * Color field (hex or named)
 */
export const colorSchema = z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$|^[a-z]+$/i)
    .optional();
// ============================================================================
// Numeric Schemas
// ============================================================================
/**
 * Positive number (> 0)
 */
export const positiveNumberSchema = z.number().positive();
/**
 * Non-negative number (>= 0)
 */
export const nonNegativeNumberSchema = z.number().min(0);
/**
 * Rating 1-5
 */
export const ratingSchema = z.number().min(1).max(5);
/**
 * Percentage 0-100
 */
export const percentageSchema = z.number().min(0).max(100);
/**
 * Priority p1-p4 (p1 highest)
 */
export const prioritySchema = z.enum(['p1', 'p2', 'p3', 'p4']);
// ============================================================================
// Mood Schema
// ============================================================================
/**
 * Standard mood values
 */
export const moodSchema = z.enum([
    'TERRIBLE',
    'BAD',
    'NEUTRAL',
    'GOOD',
    'GREAT',
]);
// ============================================================================
// Sorting Schemas
// ============================================================================
/**
 * Sort direction
 */
export const sortDirectionSchema = z.enum(['asc', 'desc']);
/**
 * Generic sort input
 */
export const sortSchema = z.object({
    sortBy: z.string().optional(),
    sortDirection: sortDirectionSchema.default('desc'),
});
// ============================================================================
// Combined Common Schemas
// ============================================================================
/**
 * Standard list query with search, pagination, and date range
 */
export const standardListSchema = cursorPaginationSchema
    .merge(searchSchema)
    .merge(dateRangeSchema)
    .merge(tagsFilterSchema);
/**
 * Standard list query with days-based lookback
 */
export const standardListWithDaysSchema = cursorPaginationSchema
    .merge(searchSchema)
    .merge(daysFilterSchema);
